import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMessageBox } from '../../contexts/MessageBoxContext';
import {
  CalendarPlottedEvent,
  EnterpriseCalendarEventUpsertPayload,
} from '../../services/enterpriseCalendarsApi';
import { ModalPortalShell } from '../ui/ModalPortalShell';
import { RECURRENCE_TYPE_OPTIONS, WEEKDAY_OPTIONS } from './calendarRecurrenceUtils';
import { formatInputDate, formatTimeForInput } from './calendarUtils';

type CalendarEventModalProps = {
  open: boolean;
  calendarName: string;
  initialDate?: Date;
  initialHour?: number;
  editingEvent?: CalendarPlottedEvent | null;
  onClose: () => void;
  onSubmit: (payload: EnterpriseCalendarEventUpsertPayload) => Promise<void>;
  onDelete?: () => Promise<void>;
};

function buildDefaultForm(initialDate?: Date, initialHour?: number): EnterpriseCalendarEventUpsertPayload {
  const date = initialDate ?? new Date();
  const hasHour = initialHour !== undefined;
  const startHour = hasHour ? String(initialHour).padStart(2, '0') : '09';
  const endHour = hasHour ? String(Math.min(initialHour + 1, 23)).padStart(2, '0') : '10';

  return {
    name: '',
    type: 'Event',
    eventDate: formatInputDate(date),
    isAllDay: !hasHour,
    startTime: hasHour ? `${startHour}:00` : undefined,
    endTime: hasHour ? `${endHour}:00` : undefined,
    recurrenceType: 'None',
    recurrenceInterval: 15,
    recurrenceDaysOfWeek: String(date.getDay()),
    excludeFromSla: false,
  };
}

function buildFormFromEvent(event: CalendarPlottedEvent): EnterpriseCalendarEventUpsertPayload {
  return {
    name: event.name,
    description: event.description,
    type: event.type,
    eventDate: event.eventDate.slice(0, 10),
    isAllDay: event.isAllDay,
    startTime: formatTimeForInput(event.startTime),
    endTime: formatTimeForInput(event.endTime),
    recurrenceType: event.recurrenceType || (event.isRecurringYearly ? 'Annual' : 'None'),
    recurrenceInterval: event.recurrenceInterval,
    recurrenceDaysOfWeek: event.recurrenceDaysOfWeek,
    excludeFromSla: event.excludeFromSla,
  };
}

export function CalendarEventModal({
  open,
  calendarName,
  initialDate,
  initialHour,
  editingEvent,
  onClose,
  onSubmit,
  onDelete,
}: CalendarEventModalProps) {
  const { confirmDelete: requestDeleteConfirmation } = useMessageBox();
  const isEditing = editingEvent != null;
  const [form, setForm] = useState<EnterpriseCalendarEventUpsertPayload>(
    buildDefaultForm(initialDate, initialHour),
  );
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const selectedWeekday = useMemo(
    () => Number.parseInt(form.recurrenceDaysOfWeek ?? '0', 10),
    [form.recurrenceDaysOfWeek],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm(
      editingEvent
        ? buildFormFromEvent(editingEvent)
        : buildDefaultForm(initialDate, initialHour),
    );
  }, [open, initialDate, initialHour, editingEvent]);

  if (!open) return null;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!form.name.trim()) return;

    if (!form.isAllDay && (!form.startTime || !form.endTime)) {
      return;
    }

    setSaving(true);
    try {
      const startTime = form.isAllDay || !form.startTime ? undefined : toTimeSpan(form.startTime);
      const endTime = form.isAllDay || !form.endTime ? undefined : toTimeSpan(form.endTime);

      await onSubmit({
        ...form,
        name: form.name.trim(),
        excludeFromSla: form.type === 'Holiday' ? true : form.excludeFromSla,
        startTime,
        endTime,
        recurrenceInterval:
          form.recurrenceType === 'CustomInterval' ? form.recurrenceInterval : undefined,
        recurrenceDaysOfWeek:
          form.recurrenceType === 'CustomWeekday'
            ? String(selectedWeekday)
            : form.recurrenceType === 'Weekly'
              ? String(new Date(`${form.eventDate}T00:00:00`).getDay())
              : undefined,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!onDelete) return;

    if (
      !(await requestDeleteConfirmation({
        title: 'Excluir evento',
        message: 'Excluir este evento permanentemente?',
      }))
    ) {
      return;
    }

    setDeleting(true);
    try {
      await onDelete();
      onClose();
    } finally {
      setDeleting(false);
    }
  }

  return (
    <ModalPortalShell
      open={open}
      panelClassName="max-w-lg rounded-2xl bg-white shadow-xl"
      overlayClassName="z-50"
    >
        <form onSubmit={(event) => void handleSubmit(event)} className="flex min-h-0 flex-1 flex-col">
          <div className="shrink-0 border-b border-slate-100 px-6 py-4">
            <h2 className="text-lg font-semibold text-slate-900">
              {isEditing ? 'Editar evento' : 'Novo evento'}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Calendário: {isEditing ? editingEvent?.calendarName ?? calendarName : calendarName}
            </p>
          </div>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-4">
            <label className="block text-sm">
              <span className="mb-1 block text-slate-600">Nome</span>
              <input
                className="w-full rounded-xl border border-slate-300 px-3 py-2"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </label>

            <label className="block text-sm">
              <span className="mb-1 block text-slate-600">Tipo</span>
              <select
                className="w-full rounded-xl border border-slate-300 px-3 py-2"
                value={form.type}
                onChange={(e) =>
                  setForm({
                    ...form,
                    type: e.target.value,
                    recurrenceType: e.target.value === 'Holiday' ? 'Annual' : form.recurrenceType,
                    isAllDay: e.target.value === 'Holiday' ? true : form.isAllDay,
                  })
                }
              >
                <option value="Holiday">Feriado</option>
                <option value="Event">Evento</option>
              </select>
            </label>

            <label className="block text-sm">
              <span className="mb-1 block text-slate-600">Data de início</span>
              <input
                type="date"
                className="w-full rounded-xl border border-slate-300 px-3 py-2"
                value={form.eventDate}
                onChange={(e) => setForm({ ...form, eventDate: e.target.value })}
                required
              />
            </label>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isAllDay ?? true}
                onChange={(e) =>
                  setForm({
                    ...form,
                    isAllDay: e.target.checked,
                    startTime: e.target.checked ? undefined : form.startTime ?? '09:00',
                    endTime: e.target.checked ? undefined : form.endTime ?? '10:00',
                  })
                }
              />
              Dia inteiro
            </label>

            {!form.isAllDay ? (
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="mb-1 block text-slate-600">Início</span>
                  <input
                    type="time"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2"
                    value={form.startTime ?? ''}
                    onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                    required
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-slate-600">Fim</span>
                  <input
                    type="time"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2"
                    value={form.endTime ?? ''}
                    onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                    required
                  />
                </label>
              </div>
            ) : null}

            <label className="block text-sm">
              <span className="mb-1 block text-slate-600">Recorrência</span>
              <select
                className="w-full rounded-xl border border-slate-300 px-3 py-2"
                value={form.recurrenceType ?? 'None'}
                onChange={(e) => setForm({ ...form, recurrenceType: e.target.value })}
              >
                {RECURRENCE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {form.recurrenceType === 'CustomInterval' ? (
              <label className="block text-sm">
                <span className="mb-1 block text-slate-600">Repetir a cada (dias)</span>
                <input
                  type="number"
                  min={1}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={form.recurrenceInterval ?? 15}
                  onChange={(e) =>
                    setForm({ ...form, recurrenceInterval: Number.parseInt(e.target.value, 10) || 1 })
                  }
                  required
                />
              </label>
            ) : null}

            {form.recurrenceType === 'CustomWeekday' ? (
              <label className="block text-sm">
                <span className="mb-1 block text-slate-600">Dia da semana</span>
                <select
                  className="w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={selectedWeekday}
                  onChange={(e) =>
                    setForm({ ...form, recurrenceDaysOfWeek: e.target.value })
                  }
                >
                  {WEEKDAY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>

          <div className="erp-btn-actions shrink-0 border-t border-slate-100 px-6 py-4">
            {isEditing && onDelete ? (
              <button
                type="button"
                className="erp-btn-danger"
                onClick={() => void handleDelete()}
                disabled={saving || deleting}
              >
                {deleting ? 'Excluindo...' : 'Excluir'}
              </button>
            ) : null}
            <button
              type="button"
              className="erp-btn-secondary"
              onClick={onClose}
              disabled={saving || deleting}
            >
              Cancelar
            </button>
            <button type="submit" disabled={saving || deleting} className="erp-btn-primary">
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
    </ModalPortalShell>
  );
}

function toTimeSpan(value: string): string {
  return value.length === 5 ? `${value}:00` : value;
}
